import { PlusIcon } from '@phosphor-icons/react'
import { BaseEdge, EdgeLabelRenderer, getBezierPath, type EdgeProps } from '@xyflow/react'
import { Button } from 'app/components/ui/button'
import { MoveHereButton } from './MoveHereButton'
import { useWorkflowActions } from './WorkflowActions'

/** Edge with a hover-revealed control at its midpoint to insert a step between the two nodes. In the
 *  auto-connect model links aren't broken by hand (that would orphan the downstream) — deleting a node
 *  bridges around it instead. Hidden until you hover, so the canvas stays clean. While a step is being
 *  moved, a valid connector shows a "Move here" target in its place. */
export function ConnectorEdge({
  id,
  source,
  target,
  sourceHandleId,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  style,
  label,
}: EdgeProps) {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  })
  const { insertOnEdge, moving, dropMove } = useWorkflowActions()
  const moveTarget = !!moving && moving.spliceable && !moving.travelling.has(source) && !moving.travelling.has(target)

  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        <div
          className="nodrag nopan group absolute flex items-center justify-center"
          style={{
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: `all`,
            minWidth: 56,
            height: 34,
          }}
        >
          {moveTarget ? (
            <MoveHereButton onClick={() => dropMove({ kind: `edge`, edgeId: id })} />
          ) : (
            <>
              {label && (
                <span className="rounded bg-surface px-1 text-[10px] font-semibold text-subtle transition-opacity group-hover:opacity-0">
                  {label}
                </span>
              )}
              {!moving && (
                <div className="absolute flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <Button
                    variant="white"
                    shape="pill"
                    title="Insert step here"
                    aria-label="Insert step here"
                    onClick={(e) =>
                      insertOnEdge({ id, source, target, sourceHandle: sourceHandleId }, { x: e.clientX, y: e.clientY })
                    }
                    className="size-5 border-strong p-0 text-subtle shadow-sm hover:border-(--fg-subtle) hover:bg-surface hover:text-strong"
                  >
                    <PlusIcon size={11} weight="bold" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  )
}
